import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { styled } from 'styled-components';
import { Button, textStyles, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';

type Props = {
    children: ReactNode;
};

type State = {
    hasError: boolean;
};

const ChunkErrorFallback = ({ onRetry }: { onRetry: () => void }) => {
    const { translate } = useTranslate();

    return (
        <Wrapper>
            <Title>{translate('chunk-error.title')}</Title>
            <Description>{translate('chunk-error.description')}</Description>
            <Button variant="secondary" onClick={onRetry}>
                {translate('chunk-error.retry')}
            </Button>
        </Wrapper>
    );
};

class ChunkErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(error: Error): Partial<State> | null {
        if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
            return { hasError: true };
        }

        return null;
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('ChunkErrorBoundary caught error:', error, info);
    }

    handleRetry = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return <ChunkErrorFallback onRetry={this.handleRetry} />;
        }

        return this.props.children;
    }
}

export default ChunkErrorBoundary;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 12px;
    padding: 48px;
`;

const Title = styled.h2`
    ${textStyles.headingM}
    color: ${token.colorText};
    margin: 0;
`;

const Description = styled.p`
    ${textStyles.uiTextM}
    color: ${token.colorTextMuted};
    margin: 0;
    text-align: center;
`;
